/**
 * Promotion API — thin wrappers around admin_coupon backend endpoints.
 *
 * Uses getERPClient().call() for mode-aware API calls (JWT or session).
 * Tenant is read from auth store at call time (late-binding).
 */

import { getERPClient } from "@/lib/erp/client";
import { useAuthStore } from "@/lib/store/authStore";
import type {
  PromotionListResponse,
  PromotionDetail,
  CreatePromotionInput,
  CreatePromotionResponse,
  UpdatePromotionInput,
  MutationResponse,
  IssueCouponResponse,
  CouponAnalyticsResponse,
} from "./promotionTypes";

const API_BASE = "zaviago_backend.storefront.admin_coupon";

function getTenant(): string {
  const tenant = useAuthStore.getState().currentTenant;
  if (!tenant) throw new Error("No tenant selected");
  return tenant;
}

export const promotionApi = {
  list(start = 0, pageLength = 20) {
    const client = getERPClient();
    return client.call<PromotionListResponse>(
      `${API_BASE}.get_promotion_list`,
      { tenant: getTenant(), start, page_length: pageLength },
    );
  },

  detail(couponCode: string) {
    const client = getERPClient();
    return client.call<PromotionDetail>(
      `${API_BASE}.get_promotion_detail`,
      { tenant: getTenant(), coupon_code: couponCode },
    );
  },

  create(data: CreatePromotionInput) {
    const client = getERPClient();
    return client.call<CreatePromotionResponse>(
      `${API_BASE}.create_promotion`,
      { tenant: getTenant(), ...data },
    );
  },

  update(couponCode: string, data: UpdatePromotionInput) {
    const client = getERPClient();
    return client.call<MutationResponse>(
      `${API_BASE}.update_promotion`,
      { tenant: getTenant(), coupon_code: couponCode, ...data },
    );
  },

  end(couponCode: string) {
    const client = getERPClient();
    return client.call<MutationResponse>(
      `${API_BASE}.end_promotion`,
      { tenant: getTenant(), coupon_code: couponCode },
    );
  },

  issueCouponToGroupNow(couponCode: string, company: string) {
    const client = getERPClient();
    return client.call<IssueCouponResponse>(
      "zaviago_backend.api.coupon_settings.issue_coupon_to_group_now",
      { coupon_code: couponCode, company },
    );
  },

  analytics() {
    const client = getERPClient();
    return client.call<CouponAnalyticsResponse>(
      "zaviago_backend.api.coupon_analytics.get_coupon_analytics",
      { company: getTenant() },
    );
  },
};
